/*
  Wishlist on Swym's JS SDK. The Wishlist Plus app embed runs in SDK-only mode ("Show Swym UI"
  off), so Swym renders nothing itself: this file is the API the theme's own wishlist UI uses.
  Swym keeps one list per shopper (multiple wishlists are disabled). Each saved size is its own
  item (product empi + variant epi), so several sizes of one product can be saved.
*/
(function () {
  const LIST_NAME = 'My Wishlist';
  let listRequest = null;

  function swat() {
    return new Promise((resolve) => {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(resolve);
    });
  }

  function call(method, ...args) {
    return swat().then((sdk) => new Promise((resolve, reject) => sdk.api[method](...args, resolve, reject)));
  }

  function fetchList(refresh) {
    if (!listRequest || refresh) {
      listRequest = swat()
        .then((sdk) => new Promise((resolve, reject) => sdk.api.fetchLists({ callbackFn: resolve, errorFn: reject })))
        .then((lists) => (lists && lists[0]) || null);
      listRequest.catch(() => {
        listRequest = null;
      });
    }
    return listRequest;
  }

  async function ensureList() {
    const list = await fetchList();
    if (list) return list;
    const created = await call('createList', { lname: LIST_NAME });
    listRequest = Promise.resolve({ ...created, listcontents: [] });
    return listRequest;
  }

  async function items() {
    const list = await fetchList();
    return (list && list.listcontents) || [];
  }

  const matches = (item, productId, variantId) =>
    String(item.empi) === String(productId) && (variantId == null || String(item.epi) === String(variantId));

  // Saved at all (any size) when variantId is omitted; that exact size otherwise.
  async function has(productId, variantId) {
    return (await items()).some((item) => matches(item, productId, variantId));
  }

  async function savedVariants(productId) {
    return (await items()).filter((item) => matches(item, productId)).map((item) => String(item.epi));
  }

  async function add({ productId, variantId, url }) {
    const list = await ensureList();
    await call('addToList', list.lid, { empi: Number(productId), epi: Number(variantId), du: url });
    await fetchList(true);
    document.dispatchEvent(new CustomEvent('wishlist:change'));
  }

  // Removes one size when variantId is given, every saved size of the product otherwise.
  async function remove(productId, variantId) {
    const list = await fetchList();
    if (!list) return;
    const found = (list.listcontents || []).filter((item) => matches(item, productId, variantId));
    for (const item of found) {
      await call('deleteFromList', list.lid, { empi: item.empi, epi: item.epi, du: item.du });
    }
    await fetchList(true);
    document.dispatchEvent(new CustomEvent('wishlist:change'));
  }

  window.BluorngWishlist = { items, has, savedVariants, add, remove };

  /*
    [data-wishlist-toggle] elements toggle a size in the wishlist. The PDP bookmark acts on the size
    selected in its product form; card bookmarks (data-wishlist-pick-size) ask for the size.
  */
  function selectedVariantId(toggle) {
    // PDP (and quick-add popup): the variant chosen in its own form. Cards: their data-variant-id.
    const productInfo = toggle.closest('product-info');
    const input = productInfo && productInfo.querySelector('form[action*="/cart/add"] input[name="id"]');
    return (input && input.value) || toggle.dataset.variantId;
  }

  function render(toggle, saved) {
    toggle.classList.toggle('red-svg', saved);
    toggle.setAttribute('aria-pressed', String(saved));
  }

  /*
    Cards (data-wishlist-pick-size) ask for the size first, in a modal styled like Swym's.
    Sizes are fetched on tap, so cards carry no variant data. Sizes already saved are marked and
    the button switches to "Remove From List" for them. Resolves to { variantId, saved }, or null
    if the shopper closes it.
  */
  async function pickSize(toggle) {
    const response = await fetch(`${new URL(toggle.dataset.productUrl, window.location.origin).pathname}.js`);
    const product = await response.json();
    const saved = await savedVariants(toggle.dataset.productId);
    const isSaved = (variant) => saved.includes(String(variant.id));
    if (product.variants.length === 1) return { variantId: product.variants[0].id, saved: isSaved(product.variants[0]) };

    return new Promise((resolve) => {
      const labels = toggle.dataset;
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      let selected =
        product.variants.find((v) => v.available && !isSaved(v)) ||
        product.variants.find((v) => !isSaved(v)) ||
        product.variants[0];

      const overlay = document.createElement('div');
      overlay.className = 'wishlist-size-modal';
      const dialog = document.createElement('div');
      dialog.className = 'wishlist-size-modal__dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-label', product.title);

      const title = document.createElement('p');
      title.className = 'wishlist-size-modal__title';
      title.textContent = product.title;
      const price = document.createElement('p');
      price.className = 'wishlist-size-modal__price';
      const label = document.createElement('p');
      label.className = 'wishlist-size-modal__label';
      label.textContent = (product.options && product.options[0] && product.options[0].name) || labels.pickLabel;
      const options = document.createElement('div');
      options.className = 'wishlist-size-modal__options';
      const submit = document.createElement('button');
      submit.type = 'button';
      submit.className = 'wishlist-size-modal__submit';
      const showSubmit = () => (submit.textContent = isSaved(selected) ? labels.pickRemove : labels.pickSubmit);
      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'wishlist-size-modal__close';
      closeButton.setAttribute('aria-label', labels.pickClose);
      closeButton.innerHTML = '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 10 10"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

      const showPrice = () => (price.textContent = `${currency} ${(selected.price / 100).toFixed(2)}`.trim());
      product.variants.forEach((variant) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'wishlist-size-modal__option';
        option.textContent = variant.title;
        option.setAttribute('aria-pressed', String(variant === selected));
        if (isSaved(variant)) {
          option.classList.add('is-saved');
          option.setAttribute('aria-description', labels.pickSaved);
        }
        option.addEventListener('click', () => {
          selected = variant;
          options.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b === option)));
          showPrice();
          showSubmit();
        });
        options.append(option);
      });
      showPrice();
      showSubmit();

      const close = (result) => {
        overlay.remove();
        document.removeEventListener('keydown', onEscape);
        toggle.focus();
        resolve(result);
      };
      const onEscape = (event) => event.key === 'Escape' && close(null);
      overlay.addEventListener('click', (event) => event.target === overlay && close(null));
      closeButton.addEventListener('click', () => close(null));
      submit.addEventListener('click', () => close({ variantId: selected.id, saved: isSaved(selected) }));
      document.addEventListener('keydown', onEscape);

      dialog.append(closeButton, title, price, label, options, submit);
      overlay.append(dialog);
      document.body.append(overlay);
      options.querySelector('[aria-pressed="true"]').focus();
    });
  }

  async function onToggle(toggle) {
    if (toggle.dataset.busy) return;
    toggle.dataset.busy = 'true';
    const productId = toggle.dataset.productId;
    const pressed = toggle.getAttribute('aria-pressed') === 'true';
    try {
      let choice;
      if (toggle.hasAttribute('data-wishlist-pick-size')) {
        choice = await pickSize(toggle);
        if (!choice) return;
      } else {
        choice = { variantId: selectedVariantId(toggle), saved: pressed };
      }
      if (choice.saved) await remove(productId, choice.variantId);
      else await add({ productId, variantId: choice.variantId, url: toggle.dataset.productUrl });
    } catch (error) {
      render(toggle, pressed);
      console.error('Wishlist update failed', error);
    } finally {
      delete toggle.dataset.busy;
      syncToggles(toggle);
    }
  }

  // Delegated, so toggles added later (the quick-add popup renders PDP markup) work too.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) onToggle(toggle);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (!toggle || toggle.tagName === 'BUTTON') return; // real buttons already click on Enter/Space
    event.preventDefault();
    onToggle(toggle);
  });

  function syncToggles(root) {
    const toggles = root.querySelectorAll ? [...root.querySelectorAll('[data-wishlist-toggle]')] : [];
    if (root.matches && root.matches('[data-wishlist-toggle]')) toggles.push(root);
    // Cards show "any size saved"; the PDP bookmark shows the size currently selected.
    toggles.forEach((toggle) => {
      const variantId = toggle.hasAttribute('data-wishlist-pick-size') ? undefined : selectedVariantId(toggle);
      has(toggle.dataset.productId, variantId).then((saved) => render(toggle, saved));
    });
  }

  // PDP: re-check the bookmark when the shopper picks another size.
  const resyncProductInfo = (event) => {
    const productInfo = event.target.closest && event.target.closest('product-info');
    if (productInfo) setTimeout(() => syncToggles(productInfo), 400);
  };
  document.addEventListener('change', resyncProductInfo);

  document.addEventListener('DOMContentLoaded', () => {
    syncToggles(document);
    // constants.js / pubsub.js globals (PUB_SUB_EVENTS is a top-level const, not a window property)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.variantChange, () => syncToggles(document));
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.matches('[data-wishlist-toggle]') || node.querySelector('[data-wishlist-toggle]'))) syncToggles(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  });
  document.addEventListener('wishlist:change', () => syncToggles(document));
})();
